import { Timestamp, GeoPoint, DocumentReference } from 'firebase/firestore'
import type { LatLng } from '@/types/structs'

/* Port of mapFromFirestore (backend/schema/util/firestore_util.dart):
 *  - Timestamp  -> Date        (Dart: -> DateTime)
 *  - GeoPoint   -> {lat, lng}  (Dart: -> LatLng)
 *  - nested maps recursively; arrays element-wise
 *  - DocumentReference passes through untouched
 *  - dotted keys ('a.b') collapse into nested objects (mergeNestedFields)
 *  - the 'firestoreUtilData' bookkeeping key is dropped
 *
 * safeGet lives in the hooks: a document whose conversion THROWS is logged
 * and silently dropped from query results (lists may have < limit items). */

export function normalizeValue(v: unknown): unknown {
  if (v instanceof Timestamp) return v.toDate()
  if (v instanceof GeoPoint) return { lat: v.latitude, lng: v.longitude } satisfies LatLng
  if (v instanceof DocumentReference) return v
  if (Array.isArray(v)) return v.map(normalizeValue)
  if (v !== null && typeof v === 'object') return normalizeMap(v as Record<string, unknown>)
  return v
}

export function normalizeMap(data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, raw] of Object.entries(data)) {
    if (key === 'firestoreUtilData') continue
    const value = normalizeValue(raw)
    if (key.includes('.')) {
      // mergeNestedFields: 'a.b.c': v -> {a: {b: {c: v}}}, merged over existing.
      const parts = key.split('.')
      let node = out
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i]!
        if (typeof node[p] !== 'object' || node[p] === null) node[p] = {}
        node = node[p] as Record<string, unknown>
      }
      node[parts[parts.length - 1]!] = value
    } else {
      out[key] = value
    }
  }
  return out
}

/* mapToFirestore counterpart — only what the app actually writes (the
 * reassurance doc): Date -> Timestamp, LatLng-shaped -> GeoPoint. */
export function denormalizeValue(v: unknown): unknown {
  if (v instanceof Date) return Timestamp.fromDate(v)
  if (isLatLngShape(v)) return new GeoPoint(v.lat, v.lng)
  if (Array.isArray(v)) return v.map(denormalizeValue)
  if (v instanceof DocumentReference) return v
  if (v !== null && typeof v === 'object') {
    return Object.fromEntries(
      Object.entries(v as Record<string, unknown>).map(([k, x]) => [k, denormalizeValue(x)]),
    )
  }
  return v
}

function isLatLngShape(v: unknown): v is LatLng {
  return (
    v !== null &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    typeof (v as LatLng).lat === 'number' &&
    typeof (v as LatLng).lng === 'number' &&
    Object.keys(v).length === 2
  )
}

/** `.withoutNulls` from firestore_util.dart: strips null/undefined entries —
 * an absent field, not a null one (reassurance alertId relies on this). */
export function withoutNulls<T extends Record<string, unknown>>(data: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== null && v !== undefined),
  ) as Partial<T>
}
